import { ChangeEvent, FormEvent, SyntheticEvent, useState } from "react";
import agent from "../../../api/agent";
import { LoginCredential } from "../../models/Login/LoginCredential";
import LoadingComponent from "./LoadingComponent";
import {
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { RegisterCredential } from "../../models/Login/RegisterCredential";
import { showToast } from "./ToastMessage";
import { useNavigate } from "react-router-dom";

interface ValidationErrorsForLogin {
  email?: string;
  password?: string;
}

interface ValidationErrorsForRegister {
  email?: string;
  userName?: string;
  companyCode?: string;
  password?: string;
  confirmPassword?: string;
}

export default function LoginPage() {
  const initialStateForLogin = {
    email: "",
    password: "",
  };
  const initialStateForRegister = {
    email: "",
    userName: "",
    companyCode: "",
    password: "",
    confirmPassword: "",
  };
  const [loginData, setLoginData] =
    useState<LoginCredential>(initialStateForLogin);
  const [activationData, setActivationData] = useState<
    RegisterCredential & { confirmPassword: string }
  >(initialStateForRegister);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [errorsForLogin, setErrorsForLogin] =
    useState<ValidationErrorsForLogin>({});
  const [errorsForRegister, setErrorsForRegister] =
    useState<ValidationErrorsForRegister>({});
  const navigate = useNavigate();

  function handleTabChange(event: SyntheticEvent, selectedValue: number) {
    setActiveTab(selectedValue);
    // set the data back to initial state once user switch tab
    setErrorsForLogin({});
    setErrorsForRegister({});
    setLoginData(initialStateForLogin);
    setActivationData(initialStateForRegister);
  }

  function handleLoginChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  }

  function handleActivationChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setActivationData((prev) => ({ ...prev, [name]: value }));
  }

  const validateLogin = (): ValidationErrorsForLogin => {
    const newErrors: ValidationErrorsForLogin = {};
    if (!loginData.email) newErrors.email = "Email is required";
    if (!loginData.password) newErrors.password = "Password is required";
    return newErrors;
  };

  const validateActivation = (): ValidationErrorsForRegister => {
    const newErrors: ValidationErrorsForRegister = {};
    let ClearConfirmPassword = false;

    if (!activationData.email) newErrors.email = "Email is required";
    if (!activationData.userName) newErrors.email = "User Name is required";
    if (!activationData.companyCode)
      newErrors.companyCode = "Company Code is required"; //Previously are refer to newErrors.companyCode
    if (!activationData.password) newErrors.password = "Password is required";

    // Password validation
    if (activationData.password) {
      if (activationData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters long";
        ClearConfirmPassword = true;
      } else if (!/(?=.*[a-z])/.test(activationData.password)) {
        newErrors.password =
          "Password must contain at least one lowercase letter";
        ClearConfirmPassword = true;
      } else if (!/(?=.*[A-Z])/.test(activationData.password)) {
        newErrors.password =
          "Password must contain at least one uppercase letter";
        ClearConfirmPassword = true;
      } else if (!/(?=.*\d)/.test(activationData.password)) {
        newErrors.password = "Password must contain at least one number";
        ClearConfirmPassword = true;
      }
    }

    if (!activationData.confirmPassword) {
      newErrors.password = "Please type again your password";
    } else if (activationData.password !== activationData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (ClearConfirmPassword && activationData.confirmPassword) {
        setActivationData(prev => ({
            ...prev,
            confirmPassword: ""
        }));
    }

    return newErrors;
  };

  async function handleLoginSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validateLogin();
    if (Object.keys(validationErrors).length > 0) {
      setErrorsForLogin(validationErrors);
      return;
    }
    setLoading(true);
    try {
      const response = await agent.Authentication.login(loginData);
      showToast("Successfully login");
      const token = response.data;
      localStorage.setItem("token", token);
      const decodedToken: any = jwtDecode(token);
      const userId = decodedToken.sub;
      // to get the user role for user logged in
      const userDetailsResponse = await agent.User.getUserDetail(userId, token);
      const companyId = userDetailsResponse.companyId;

      // navigate to subscription page if normal user login
      if (userDetailsResponse.role === "User") {
        navigate("/subscription");
      } else if (userDetailsResponse.role === "Admin") {
        navigate(`/company/details/${companyId}`);
      }
      else {
        //   Navigate to home page
        navigate("/softwares");
      }
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data;
        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Invalid credentials")
        ) {
          showToast("Wrong Email or Password", "error");
          setErrorsForLogin({
            email: "Wrong email",
            password: "Wrong password",
          });
          setLoginData({
            ...loginData,
            password: "",
          });
        }
      }
      console.error("Login failed:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleActivationSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validateActivation();
    if (Object.keys(validationErrors).length > 0) {
      setErrorsForRegister(validationErrors);
      return;
    }
    setLoading(true);
    const backendData: RegisterCredential = {
      email: activationData.email,
      userName: activationData.userName,
      companyCode: activationData.companyCode,
      password: activationData.password,
    };
    try {
      // Process to register new user
      await agent.Authentication.accountActivation(backendData);
      //Toast message to show register sucess
      showToast("New Account Activated Successfully", "success");

      // Goes to login tab (Login form)
      setActiveTab(0);
    } catch (error: any) {
      if (error.response) {
        const errorMessage = error.response.data;

        if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Email already exists")
        ) {
          showToast("Email already exists", "error");
          setErrorsForRegister({
            email: "Email already exists",
          });
        }
        // Check if it's the domain validation error
        else if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Invalid Email domain")
        ) {
          showToast("Invalid email domain", "error");
          setErrorsForRegister({
            email:
              "Your email domain is not allowed for this company. Please use your company email address.",
          });
        }
        // Check if it's the company code error
        else if (
          typeof errorMessage === "string" &&
          errorMessage.includes("Invalid company code")
        ) {
          showToast("Invalid company code", "error");
          setErrorsForRegister({
            companyCode: "Invalid company code",
          });
        } else {
          // Display whatever error message came from the backend
          alert(errorMessage || "Registration failed");
        }
      } else {
        alert("Network error - please try again later");
        console.error("Register failed:", error);
      }
    } finally {
      setLoading(false);
    }
  }

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading..." />;

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          mt: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: "100%",
            maxWidth: 600,
            margin: "0 auto",
            borderRadius: 10,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
              py: 4,
            }}
          >
            <Avatar
              alt="UAS"
              src="/UASLOGO.png"
              sx={{
                width: 100, // Larger size
                height: 100,
                mb: 3, // Space between logo and text
                boxShadow: 3, // Subtle depth
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)", // Gentle hover effect
                },
              }}
            />

            {/* Modern Typography with gradient text */}
            <Typography
              variant="h4"
              fontWeight="bold"
              textAlign="center"
              sx={{ color: "#4dabf5" }}
            >
              Welcome to UAS License Portal
            </Typography>
          </Box>
          <Divider />

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            indicatorColor="primary"
            textColor="primary"
            aria-label="Login and account activation tabs"
            sx={{ mb: 3 }}
          >
            <Tab label="Login" />
            <Tab label="Account Activation" />
          </Tabs>

          {activeTab === 0 ? (
            <Box component="form" onSubmit={handleLoginSubmit} sx={{ mt: 3 }}>
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={loginData.email}
                onChange={handleLoginChange}
                error={!!errorsForLogin.password}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />
              <TextField
                label="Password"
                name="password"
                type="password"
                value={loginData.password}
                onChange={handleLoginChange}
                error={!!errorsForLogin.password}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />

              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Sign In
                </Button>
              </Box>

              {/* Link for switch the tab to account activation */}
              <Box
                sx={{
                  mt: 2,
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ mr: 1 }}>
                  Don't have an account?
                </Typography>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => setActiveTab(1)} // Switch to next tab(new Account registeration)
                  sx={{
                    color: "primary.main",
                    fontWeight: "medium",
                    "&:hover": {
                      textDecoration: "underline",
                      cursor: "pointer",
                    },
                  }}
                >
                  Create Account
                </Link>
              </Box>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleActivationSubmit}
              sx={{ mt: 2 }}
            >
              <TextField
                label="Email Address"
                name="email"
                type="email"
                value={activationData.email}
                onChange={handleActivationChange}
                error={!!errorsForRegister.email}
                helperText={errorsForRegister.email}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />
              <TextField
                label="Username"
                name="userName"
                value={activationData.userName}
                onChange={handleActivationChange}
                error={!!errorsForRegister.userName}
                helperText={errorsForRegister.userName}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                inputProps={{
                  pattern: "^[a-zA-Z0-9_]{3,20}$", // Example pattern
                  title: "3-20 characters (letters, numbers, underscores)",
                }}
              />

              {/* New Company Code Field */}
              <TextField
                label="Company Code"
                name="companyCode"
                value={activationData.companyCode}
                onChange={handleActivationChange}
                error={!!errorsForRegister.companyCode}
                helperText={errorsForRegister.companyCode}
                fullWidth
                margin="normal"
                variant="outlined"
                required
                inputProps={{
                  pattern: "^\\d{6}$",
                  title: "6-digit company code",
                }}
              />

              <TextField
                label="Password"
                name="password"
                type="password"
                value={activationData.password}
                onChange={handleActivationChange}
                error={!!errorsForRegister.password}
                helperText={errorsForRegister.password}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />
              <TextField
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={activationData.confirmPassword}
                onChange={handleActivationChange}
                error={!!errorsForRegister.confirmPassword}
                helperText={errorsForRegister.confirmPassword}
                fullWidth
                margin="normal"
                variant="outlined"
                required
              />
              <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                >
                  Activate Account
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
